<script setup lang="ts">
import IcSearch from '@/components/Icons/IcSearch.vue'
import NewDateRangePicker from '../Globals/Inputs/NewDateRangePicker.vue'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import { Form, Field } from 'vee-validate'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { formatPeriodDate } from '@/utils/date/date-time'
import { useGroupStore } from '@/store/group'
import { IGroup } from '@/interfaces/entities/group'
import { useRateStore } from '@/store/rate'
import { IRateSearchBarSchema } from '@/interfaces/api/schemas/rate'

const { t } = useI18n()

const emit = defineEmits(['groupSelected'])

//Stores
const { fetch: fetchUserGroups } = useGroupStore()
const { setGroup, getPeriodFilter, fetchRate } = useRateStore()

//Ref
const formRef = ref<InstanceType<typeof Form>>()
const isLoading = ref(false)
const selectedGroup = ref<IGroup | null>(null)
const { listGroups } = storeToRefs(useGroupStore())

//Helpers
const handleGroupSelected = (group: object) => {
  const castedGroup = group as IGroup
  setGroup(castedGroup)
  selectedGroup.value = castedGroup
  emit('groupSelected', castedGroup)
}

const onSubmit = (values: object) => {
  const castedValues = values as IRateSearchBarSchema
  const formattedPeriod = formatPeriodDate(castedValues.period)

  isLoading.value = true

  fetchRate({
    groupId: castedValues.group.id,
    dateRange: formattedPeriod,
  }).finally(() => {
    isLoading.value = false
  })
}

//Lifecycle
onMounted(() => {
  fetchUserGroups()
})
</script>

<template>
  <div class="mt-6 flex flex-col flex-wrap items-start gap-3 bg-white p-6 shadow-md sm:flex-row sm:items-end">
    <Form ref="formRef" @submit="onSubmit" class="flex w-full flex-col flex-wrap gap-3 sm:flex-row sm:items-end">
      <SearchBarField :label="t('groups.title')">
        <Dropdown name="group" :options="listGroups" :label-attribute="'name'" @option-selected="handleGroupSelected" />
      </SearchBarField>

      <SearchBarField :label="t('dashboard.header.buttons.period')" class="min-w-[320px] flex-1">
        <Field name="period" v-slot="{ field }">
          <NewDateRangePicker v-bind="field" :already-selected-period="getPeriodFilter" :placeholder="t('plugins.vue_datepicker.placeholder')" class="w-full" />
        </Field>
      </SearchBarField>

      <div class="ml-auto flex shrink-0 items-center">
        <button
          :disabled="isLoading"
          class="flex flex-row items-center bg-blue px-4 py-3 text-white hover:bg-blue-2 disabled:opacity-40 disabled:hover:bg-blue">
          <IcSearch v-if="!isLoading" class="mr-2 h-5 w-5" />
          <span v-else class="mr-2 animate-spin">
            <IcAnimateSpin class="h-5 w-5 text-white" />
          </span>
          {{ t('dashboard.header.buttons.search') }}
        </button>
      </div>
    </Form>
  </div>
</template>
